import api from './api';

/**
 * Belagom Review Engine — client.
 *
 * Eén publieke wizard (/review/:token) voor beide stromen:
 *   reviewer_rol 'medewerker'  anonieme evaluatie na sessie/traject
 *   reviewer_rol 'employer'    HR-projectevaluatie na een afgeronde mijlpaal
 * Vragen: q1 Vakkennis, q2 Snelheid & Bereikbaarheid, q3 Doelgerichtheid (tekst per discipline) + open vraag.
 */

export type ReviewerRol = 'medewerker' | 'employer';

export interface ReviewQuestion {
  id: 'q1' | 'q2' | 'q3';
  label: string;
  question: string;
  lowLabel: string;
  highLabel: string;
}

export interface ReviewOpenQuestion {
  id: 'open_comment';
  label: string;
  question: string;
  placeholder: string;
  privacyNote: string;
}

export interface ReviewFormData {
  professional_name: string;
  professional_type: string | null;
  reviewer_rol?: ReviewerRol;
  bron_label?: string;
  anoniem?: boolean;
  aantal_sterrenvragen?: number;
  questions: ReviewQuestion[];
  open_question?: ReviewOpenQuestion;
}

export interface ReviewFormResponse {
  already_submitted?: boolean;
  data?: ReviewFormData;
}

export interface SubmitReview {
  token: string;
  q1?: number;
  q2?: number;
  q3?: number;
  open_comment?: string;
}

export const getReviewForm = (token: string) =>
  api.get<{ success: boolean; already_submitted?: boolean; data?: ReviewFormData }>(`/reviews/form/${token}`).then((r) => r.data as ReviewFormResponse);

export const submitReview = (body: SubmitReview) =>
  api.post<{ success: boolean; message: string; data?: { sanitizer_verwijderd?: number } }>('/reviews/submit', body).then((r) => r.data);

// ─── Medewerkersportaal: open uitnodigingen (in-app Review-Modal) ─────────────

export interface OpenReviewUitnodiging {
  id: string;
  token: string;
  bron_type: string;
  bron_label: string;
  professional_naam: string;
  discipline_label: string | null;
  aantal_sterrenvragen: number;
  created_at: string;
  token_expires_at: string;
  review_url: string;
}

export const getOpenReviewUitnodigingen = () =>
  api.get<{ data: OpenReviewUitnodiging[] }>('/medewerker/reviews/openstaand').then((r) => r.data.data ?? []);

export const stelReviewUit = (id: string, uren = 24) => api.post(`/medewerker/reviews/${id}/later`, { uren });

// ─── Employer cockpit: B2B-projectevaluaties ──────────────────────────────────

export interface B2bReview {
  id: string;
  token: string | null;
  review_url: string | null;
  project_id: string | null;
  project_code: string | null;
  project_titel: string | null;
  milestone_id: string | null;
  milestone_volgnummer: number | null;
  milestone_titel: string | null;
  professional_id: string;
  professional_naam: string;
  professional_type: string | null;
  discipline_label: string | null;
  geaccordeerd_op: string | null;
  submitted_at: string | null;
  verloopt_op: string | null;
  q1: number | null;
  q2: number | null;
  q3: number | null;
  open_comment: string | null;
  average_rating: number | null;
  questions: ReviewQuestion[];
  open_question: ReviewOpenQuestion;
}

export const getB2bReviews = () =>
  api.get<{ data: { open: B2bReview[]; ingevuld: B2bReview[] } }>('/employer/reviews/b2b').then((r) => r.data.data);

// ─── Professional: B2B-reviews per opdrachtgever ─────────────────────────────

export interface OpdrachtgeverReviewOverzicht {
  by_employer: Array<{
    employer_id: string;
    employer_name: string;
    review_count: number;
    average_rating: number;
    questions: Array<ReviewQuestion & { avg: number }>;
    mijlpalen: Array<{
      id: string;
      project_code: string | null;
      project_titel: string | null;
      milestone_volgnummer: number | null;
      milestone_titel: string | null;
      average_rating: number;
      submitted_at: string;
    }>;
    open_comments: Array<{ open_comment: string; submitted_at: string; employer_name: string; project_code: string | null }>;
  }>;
  total_reviews: number;
  total_average_rating: number | null;
}
